"use client";

import { formatInTimeZone } from "date-fns-tz";
import { differenceInMinutes, differenceInHours, differenceInDays, format } from "date-fns";
import { enUS, te } from "date-fns/locale";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FC, useEffect, useRef } from "react";
import { FaHome } from "react-icons/fa";

import VisualComponent from "@/components/blocks/SpecialEventsBlock/VisualComponent";
import { LexicalRichText } from "@/components/LexicalRichText";
import { YoutubeEmbed } from "@/components/LexicalRichText/Embed";
import Avatar from "@/components/shared/Avatar";
import CustomImage from "@/components/shared/CustomImage";
import SocialShare from "@/components/shared/SocialShare";
import TitleTag from "@/components/shared/TitleTag";
import { Typography } from "@/components/shared/Typography";
import VideoPlayer from "@/components/shared/VideoPlayer";
import { PostRecommendedNews } from "@/services/newsService";
import { ArticleType, ArticleCategoryType } from "@/types/article";
import { getLabel } from "@/data/labels";
import { getArticleImageUrl, getMediaSrcSet } from "@/utils/getImageUrl";
import { getLocaleFromPathname, getLocalePrefix } from "@/utils/getLocaleFromPath";
import { getLocalizedString } from "@/utils/getLocalizedString";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import { extractVideoId } from "@/utils/getYoutubeId";
import ArticleLikeBookmarkSection from "./ArticleLikeBookmarkSection";
import { formatDate } from "@/utils/dateUtils";

interface ArticleDetailHeroSectionProps {
  article: ArticleType;
}

const ArticleDetailHeroSection: FC<ArticleDetailHeroSectionProps> = ({ article }) => {
  const pathname = usePathname();
  const locale = getLocaleFromPathname(pathname) || "te-IN";
  const localePrefix = getLocalePrefix(pathname);
  const language =
    (locale?.split("-")[0] as AppLocale) || (article?.language === "te" ? "te" : "en");

  const newsReporter = article?.byline;
  const profilePic =
    newsReporter?.image?.filename && getArticleImageUrl(newsReporter.image, "ld");
  const videoSource = article?.isVideo
    ? article.videoSource === "youtube"
      ? extractVideoId(article?.youtubeEmbed || "")
      : article.video?.hlsPlaybackUrl?.length
        ? article.video?.hlsPlaybackUrl
        : ""
    : false;

  const heroImageSrc = getArticleImageUrl(article?.image, "md");
  const heroImageSrcSet = getMediaSrcSet(article?.image, ["sd", "md", "hd"]);
  const hasHeroImage = Boolean(heroImageSrc);

  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    PostRecommendedNews({ newsId: article?.id || "" });
  }, [article?.id]);

  // Render HTML content as-is; bold is controlled only by backend/editor (no auto first-words bold)
  const processContent = (html: string | any): string => {
    if (!html) return "";
    const htmlString = typeof html === "string" ? html : html?.html || "";
    return htmlString || "";
  };

  // Normalize graph data: API may return { data: [] } or legacy array; ensure we have { data: [...] } with at least one valid row
  const graphData = ((): { data: { col1?: string; name: string; point: string | number }[] } | null => {
    const g = article?.graphData;
    if (!g) return null;
    const withData = g as { data?: { col1?: string; name: string; point: string | number }[] };
    if (withData?.data && Array.isArray(withData.data) && withData.data.length > 0) {
      return { data: withData.data };
    }
    if (Array.isArray(g) && g.length > 0)
      return { data: g as { col1?: string; name: string; point: string | number }[] };
    return null;
  })();

  return (
    <div className="w-full">
      {/* Breadcrumb: home → category → subcategory only */}
      <header
        className={`mb-4 w-full space-y-3 antialiased text-[#0F1522] dark:text-white ${
          language === "te" ? "font-title" : "font-sans"
        }`}
      >
        <nav
          className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] sm:text-[14px] md:text-[15px] font-medium"
          aria-label="Breadcrumb"
        >
          <Link
            href={localePrefix || "/"}
            className="inline-flex items-center text-inherit opacity-[0.78] transition-opacity hover:opacity-100"
            aria-label={getLabel(language, "HOME")}
          >
            <FaHome className="text-[14px]" />
          </Link>
          <span className="text-inherit opacity-35 select-none" aria-hidden={true}>
            /
          </span>
          <Link
            href={`${localePrefix}/${article?.categoryId?.slug}`}
            className="text-inherit opacity-[0.78] transition-opacity hover:opacity-100"
            aria-label={`View all news in the category: ${
              getLocalizedString(article?.categoryId?.name, locale) || article?.categoryId?.slug
            }`}
          >
            {getLocalizedString(article?.categoryId?.name, locale) || article?.categoryId?.slug}
          </Link>
          {article?.subCategoryId && (
            <>
              <span className="text-inherit opacity-35 select-none" aria-hidden={true}>
                /
              </span>
              <Link
                href={`${localePrefix}/${article?.categoryId?.slug}/${article?.subCategoryId?.slug}`}
                className="text-inherit opacity-[0.78] transition-opacity hover:opacity-100"
                aria-label={`View all news in the subcategory: ${
                  getLocalizedString(article?.subCategoryId?.name, locale) || article?.subCategoryId?.slug
                }`}
              >
                {getLocalizedString(article?.subCategoryId?.name, locale) || article?.subCategoryId?.slug}
              </Link>
            </>
          )}
        </nav>

        {article?.isLiveNews &&
          article?.publishedAt &&
          Date.now() - new Date(article.publishedAt).getTime() <= 12 * 60 * 60 * 1000 && (
            <div className="flex flex-wrap items-center gap-2">
              <TitleTag labelKey="LIVE_NEWS" size="md" />
            </div>
          )}

        <h1
          className={`m-0 pb-0 text-inherit text-[20px] sm:text-[23px] md:text-[26px] lg:text-[30px] font-bold leading-[1.25] tracking-tight ${
            language === "te" ? "font-title" : "font-sans"
          }`}
          style={{
            fontWeight: 700,
            letterSpacing: "-0.02em",
            verticalAlign: "middle",
          }}
        >
          {article?.isInPrint && <TitleTag labelKey="PRINT_EDITION" />}
          {article?.title}
        </h1>
      </header>

      {/* Time and Share Section - same line on all viewports including mobile */}
      <div
        className="w-full mb-4 flex flex-row items-center justify-between gap-2"
        style={{ padding: "8px 0" }}
      >
        <Typography
          variant="body3"
          className="text-[#AEAEAE] dark:text-gray-400 text-xs sm:text-sm min-w-0 flex-shrink"
        >
          {article?.publishedAt
            ? (() => {
                const now = new Date();
                const published = new Date(article.publishedAt);
                const diffMinutes = differenceInMinutes(now, published);
                const diffHours = differenceInHours(now, published);
                const diffDays = differenceInDays(now, published);

                if (language === "te") {
                  if (diffMinutes < 60) {
                    return `${diffMinutes} ${diffMinutes === 1 ? "నిమిషం" : "నిమిషాల"} క్రితం`;
                  } else if (diffHours < 24) {
                    return `${diffHours} ${diffHours === 1 ? "గంట" : "గంటల"} క్రితం`;
                  } else if (diffDays < 7) {
                    return `${diffDays} ${diffDays === 1 ? "రోజు" : "రోజుల"} క్రితం`;
                  } else {
                    return formatDate(published, language);
                  }
                } else {
                  if (diffMinutes < 60) {
                    return `${diffMinutes} Min Ago`;
                  } else if (diffHours < 24) {
                    return `${diffHours} Hour${diffHours > 1 ? "s" : ""} Ago`;
                  } else if (diffDays < 7) {
                    return `${diffDays} Day${diffDays > 1 ? "s" : ""} Ago`;
                  } else {
                    return formatDate(published,language);
                  }
                }
              })()
            : ""}
        </Typography>
        <div className="flex-shrink-0">
          <SocialShare
            title={article?.title}
            url={getNewsDetailsUrl(article?.categoryId, article?.subCategoryId, article?.slug, locale)}
          />
        </div>
      </div>

      {/* 3. Image - only render when we have image or video; clamp aspect ratio to avoid huge blank space */}
      {(hasHeroImage || videoSource) && (
        <div className="mt-4">
          {article?.image?.caption && article?.image?.captionPosition === "top" && (
            <div className="pl-2 sm:pl-4 mb-2">
              <Typography
                language={language}
                variant="body2"
                className="text-[#858585] text-xs sm:text-sm"
              >
                {article?.imageCaption || article?.image?.caption}
              </Typography>
            </div>
          )}
          <div
            className="relative"
            style={{
              paddingBottom: (() => {
                const w = article?.image?.width || 16;
                const h = article?.image?.height || 9;
                const ratio = w > 0 ? (h / w) * 100 : 56.25;
                const clamped = Math.min(120, Math.max(30, ratio));
                return `${clamped}%`;
              })(),
            }}
          >
            <div className="absolute inset-0">
              {!videoSource ? (
                <CustomImage
                  src={heroImageSrc}
                  srcSet={heroImageSrcSet}
                  sizes="(max-width: 768px) 100vw, 800px"
                  alt={article?.image?.alt || ""}
                  className="object-cover w-full h-full rounded-xl"
                  phImg="/images/placeholder-grey-md.png"
                  loading="eager"
                  fetchPriority="high"
                />
              ) : typeof videoSource === "string" ? (
                <VideoPlayer
                  hlsPlaybackUrl={videoSource}
                  poster={heroImageSrc}
                />
              ) : (
                <YoutubeEmbed embedType={videoSource?.embedType} videoID={videoSource?.id} />
              )}
            </div>
          </div>

          {(article?.imageCaption || article?.image?.caption) &&
            article?.image?.captionPosition !== "top" && (
              <div className="pl-2 sm:pl-4 mt-2">
                <Typography
                  language={language}
                  variant="body2"
                  className="text-[#858585] text-xs sm:text-sm"
                >
                  {article?.imageCaption || article?.image?.caption}
                </Typography>
              </div>
            )}
        </div>
      )}

      {/* 4. WEB DESK / Author and Published Details - Below Image */}
      <div className="py-4 sm:py-6 md:py-8">
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          {newsReporter && (
            <Link
              href={`${localePrefix}/author/${newsReporter?.slug}`}
              aria-label={`View all articles by ${newsReporter?.name}`}
              className="flex-shrink-0"
            >
              <div className="hidden sm:block">
                <Avatar image={profilePic} width={60} height={60} />
              </div>
              <div className="block sm:hidden">
                <Avatar image={profilePic} width={50} height={50} />
              </div>
            </Link>
          )}

          <div className="flex-1 min-w-0">
            {newsReporter ? (
              <Link
                href={`${localePrefix}/author/${newsReporter?.slug}`}
                aria-label={`View all articles by ${newsReporter?.name}`}
              >
                <Typography
                  className="text-[16px] sm:text-[18px] md:text-[20px]"
                  variant="subtitle2"
                  language={language}
                  style={{
                    fontWeight: 700,
                    lineHeight: "100%",
                    letterSpacing: "-0.03em",
                    verticalAlign: "middle",
                  }}
                >
                  {newsReporter?.name}
                </Typography>
              </Link>
            ) : (
              <Typography
                className="font-bold text-[#0F1522] dark:text-white text-[20px] sm:text-[24px] md:text-[28px]"
                variant="subtitle2"
                language={language}
                style={{
                  fontWeight: 900,
                  lineHeight: "100%",
                  letterSpacing: "-0.03em",
                  verticalAlign: "middle",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textShadow: "0.5px 0 0 currentColor",
                }}
              >
                {language === "te" ? "వెబ్ డెస్క్" : "WEB DESK"}
              </Typography>
            )}
            <Typography
              variant="body3"
              language={language}
              className="flex flex-wrap items-center gap-1 mt-1 !text-[#676767] dark:!text-gray-300 text-xs sm:text-sm"
              style={{
                fontWeight: 500,
                lineHeight: "100%",
                letterSpacing: "-0.03em",
                verticalAlign: "middle",
              }}
            >
              {getLabel(language, "PUBLISHED_ON")}{" "}
              {formatInTimeZone(
                new Date(article?.publishedAt),
                "Asia/Kolkata",
                "MMM dd, yyyy, hh:mm a",
                {
                  locale: language === "te" ? te : enUS,
                },
              )}
              {" | "}
              {article?.readTime || 0} {getLabel(language, "MIN_READ")}
              {article?.video?.duration
                ? `|  ${language === "te" ? "చూడటం సమయం" : "Watch Time"} : ${
                    article?.video?.duration
                  }`
                : ""}
            </Typography>
          </div>
        </div>
      </div>

      {article?.hasGraph &&
        article?.graphPosition === "top" &&
        graphData?.data &&
        graphData.data.length > 0 && (
          <div className="mx-auto w-full max-w-[500px] py-4 sm:py-6 md:py-8 px-2 sm:px-0">
            <VisualComponent
              data={graphData.data}
              mode={article?.graphMode}
              title={article?.chartTitle || ""}
              tableHeaders={[
                article?.col1Header || "#",
                article?.col2Header || "Label",
                article?.col3Header || "Value",
              ]}
            />
          </div>
        )}

      <div
        className={`article-body mx-auto w-full max-w-[800px] min-[1301px]:max-w-[610px] px-2 pb-4 pt-2 sm:px-0 text-[#121212] dark:text-white ${
          language === "te"
            ? "text-lg sm:text-[19px] font-content"
            : "text-sm sm:text-base font-sans"
        }`}
        ref={contentRef}
        style={{
          fontWeight: 400,
          lineHeight: language === "te" ? "1.85" : "1.8",
          verticalAlign: "middle",
        }}
      >
        {article?.contentV2 ? (
          <LexicalRichText contentV2={article?.contentV2} language={language} />
        ) : (
          <div dangerouslySetInnerHTML={{ __html: processContent(article?.content || "") }} />
        )}
      </div>

      <div className="mx-auto w-full max-w-[500px] pb-4 sm:pb-6 md:pb-8 px-2 sm:px-0">
        {article?.hasGraph &&
          article?.graphPosition === "bottom" &&
          graphData?.data &&
          graphData.data.length > 0 && (
            <VisualComponent
              data={graphData.data}
              mode={article?.graphMode}
              title={article?.chartTitle ?? ""}
              tableHeaders={[
                article?.col1Header || "#",
                article?.col2Header || "Label",
                article?.col3Header || "Value",
              ]}
            />
          )}
      </div>

      {/* Social, Bookmark and Likes - At the Last */}
      <div className="mt-0 flex min-h-[88px] w-full items-center bg-[#F9F9F9] px-4 py-4 dark:bg-gray-800 sm:px-5 sm:py-4 md:px-6">
        <ArticleLikeBookmarkSection article={article} />
      </div>

      {/* Whatsapp section */}
      {/* <div className="mt-8">
        <WhatsappTelegramChannel language={language} />
      </div> */}
    </div>
  );
};

export default ArticleDetailHeroSection;
